import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Bitdefender.css";
import bitdefenderLogo from "../../assets/images/partners/Bitdefender.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const endpointLayers = [
  { icon: "🛡️", name: "Bitdefender GravityZone EDR", desc: "Advanced endpoint detection and response — behavioral analysis, memory protection, exploit prevention, and process injection detection without relying solely on signature databases." },
  { icon: "🔍", name: "Machine Learning Anti-Malware", desc: "Bitdefender's dual-layer machine learning models trained on hundreds of millions of malware samples — detecting new and polymorphic threats before signatures exist for them." },
  { icon: "🌐", name: "Network Attack Defense", desc: "In-kernel network-layer threat detection blocking brute force attacks, port scanners, and lateral movement attempts at the network stack before they reach the application layer." },
  { icon: "🔐", name: "Device Control & Application Control", desc: "Granular USB device control, removable media encryption enforcement, and application whitelisting/blacklisting — preventing unauthorized software execution across managed endpoints." },
  { icon: "💾", name: "Full-Disk Encryption Management", desc: "BitLocker and FileVault management through GravityZone — centralized key escrow, recovery key management, and compliance reporting for encryption policy adherence." },
];

const xdrCards = [
  { icon: "🔭", title: "Extended Detection & Response", desc: "GravityZone XDR correlates telemetry from endpoints, networks, email, cloud workloads, and identity providers — providing attack timeline visualization and root-cause analysis across the entire kill chain rather than individual signal silos." },
  { icon: "🗺️", title: "Attack Timeline Mapping", desc: "Visual attack timeline reconstructing the complete chain of events for every security incident — showing initial access vector, lateral movement paths, privilege escalation, and payload delivery with timestamps and affected system inventory." },
  { icon: "🤖", title: "AI-Powered Incident Correlation", desc: "Bitdefender's security analytics engine automatically correlates low-fidelity signals that individually fall below alert thresholds — surfacing multi-stage attacks invisible to signature-based detection through behavioral pattern recognition." },
  { icon: "🎯", title: "Threat Hunting", desc: "Structured threat hunting capabilities built into GravityZone — with curated hunting queries, cross-endpoint search, and process tree analysis enabling security teams to proactively search for indicators of compromise before automated detection fires." },
  { icon: "⚡", title: "Automated Response", desc: "GravityZone XDR automated response playbooks isolate compromised endpoints, terminate malicious processes, revoke user tokens, and trigger investigation workflows — reducing mean time to contain from hours to minutes." },
  { icon: "📊", title: "Security Posture Dashboard", desc: "Continuous security posture scoring with risk factor breakdown across patch compliance, endpoint protection status, vulnerability exposure, and configuration drift — driving prioritized remediation rather than reactive incident response." },
];

const mdrSlas = [
  { val: "< 5 min", label: "Avg Detection Time" },
  { val: "24/7", label: "SOC Coverage" },
  { val: "< 30 min", label: "MTTR Containment" },
  { val: "98%", label: "Threat Catch Rate" },
];

const aiCaps = [
  { icon: "🧠", title: "Adaptive Machine Learning", desc: "Bitdefender's adaptive ML models update locally on each endpoint — continuously improving detection accuracy based on behavioral patterns specific to your organization's environment without requiring cloud connectivity." },
  { icon: "🔬", title: "Sandbox Analyzer", desc: "Bitdefender Sandbox Analyzer detonates suspicious files in isolated virtual environments — observing runtime behavior including registry modifications, network connections, and process injection before allowing execution on production endpoints." },
  { icon: "🕵️", title: "Anomaly Detection", desc: "User and entity behavior analytics (UEBA) establishing baseline behavioral profiles for users and endpoints — flagging statistically significant deviations indicative of account compromise, insider threats, or supply chain attacks." },
  { icon: "🎛️", title: "GravityZone Security Analytics", desc: "Risk analytics continuously evaluating endpoint risk scores based on patch status, configuration, browsing behavior, and threat exposure — enabling security teams to prioritize remediation by actual organizational risk rather than severity scores alone." },
];

const complianceFrameworks = [
  { icon: "🏥", name: "HIPAA", desc: "Endpoint encryption, access logging, and audit trails supporting healthcare data protection requirements" },
  { icon: "💳", name: "PCI DSS", desc: "Anti-malware controls, network segmentation monitoring, and change detection for cardholder data environments" },
  { icon: "🏛️", name: "NIST CSF", desc: "GravityZone maps to NIST Cybersecurity Framework Identify, Protect, Detect, Respond, Recover functions" },
  { icon: "🔐", name: "SOC 2 Type II", desc: "Continuous monitoring evidence, access control reporting, and incident response documentation for SOC 2 audits" },
  { icon: "🇪🇺", name: "GDPR", desc: "Data discovery, encryption enforcement, and breach notification support for EU data protection compliance" },
  { icon: "🇺🇸", name: "CMMC 2.0", desc: "Endpoint protection controls mapped to CMMC 2.0 practices for defense contractor compliance requirements" },
  { icon: "📋", name: "ISO 27001", desc: "Technical security controls aligned to ISO 27001 Annex A requirements with evidence collection" },
  { icon: "🏦", name: "FINRA / SEC", desc: "Audit logging, change management, and security monitoring supporting financial industry regulatory requirements" },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant endpoint security protecting EMR workstations, medical IoT devices, and remote clinical staff with MDR coverage for healthcare-specific ransomware threats." },
  { icon: "🏦", name: "Financial Services", desc: "Multi-layered endpoint protection for banking and insurance organizations with regulatory compliance reporting for PCI DSS, GLBA, and SEC cybersecurity rule requirements." },
  { icon: "🏭", name: "Manufacturing", desc: "Endpoint security for OT-adjacent environments — protecting engineering workstations, HMI systems, and corporate endpoints while maintaining network segmentation between IT and OT zones." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-authorized Bitdefender GravityZone Business Security for federal agencies, state/local governments, and government contractors with CMMC 2.0 compliance alignment." },
  { icon: "🎓", name: "Education", desc: "Endpoint protection for student and faculty devices across campus environments — with FERPA-compliant security reporting and ransomware protection for research data systems." },
  { icon: "⚖️", name: "Legal & Professional", desc: "Confidential document protection, encrypted communication enforcement, and privileged access monitoring for law firms and professional services organizations handling sensitive client data." },
  { icon: "🛒", name: "Retail & eCommerce", desc: "POS endpoint protection, e-commerce server security, and cardholder data environment isolation for retail organizations with distributed store networks and online presence." },
  { icon: "🏗️", name: "Critical Infrastructure", desc: "Industrial endpoint security for utilities, energy, and critical infrastructure operators — with OT-aware threat detection and air-gap deployment capabilities for isolated operational environments." },
];

const faqData = [
  { q: "What is the difference between Bitdefender GravityZone EDR and XDR?", a: "Bitdefender GravityZone EDR (Endpoint Detection and Response) focuses specifically on endpoint telemetry — monitoring and responding to threats on Windows, macOS, and Linux endpoints. It provides behavioral detection, process analysis, threat investigation, and automated response capabilities for endpoint security events. GravityZone XDR (Extended Detection and Response) extends beyond endpoints to correlate telemetry from multiple security layers: endpoints, network traffic, email security, cloud workloads, and identity providers like Active Directory and Azure AD. XDR reconstructs complete attack timelines across all these data sources — enabling detection of multi-stage attacks that use legitimate tools and move laterally between systems in ways that look normal when viewed from any single data source. For organizations facing sophisticated adversaries or requiring compliance with regulations mandating multi-layer visibility, XDR provides substantially more comprehensive detection capability. ALG Data Guard helps organizations assess which tier aligns with their threat model, budget, and operational maturity." },
  { q: "What is Bitdefender MDR and how does it work with GravityZone?", a: "Bitdefender Managed Detection and Response (MDR) is a 24/7 security service where Bitdefender's SOC analysts actively monitor your GravityZone environment, investigate alerts, hunt for threats, and respond to incidents on your behalf. MDR operates on top of GravityZone — using the same telemetry, detection capabilities, and response tools but operated by Bitdefender security analysts rather than your internal team. When MDR detects an active threat, analysts can directly contain the threat through GravityZone (isolating endpoints, terminating processes, blocking network connections) without waiting for your team to respond. MDR includes proactive threat hunting where analysts search for indicators of compromise that haven't triggered automated alerts, regular security posture reviews, and monthly threat intelligence reports. ALG Data Guard works with Bitdefender MDR to handle the 24/7 monitoring while we focus on implementation, policy management, and strategic security improvement for your organization." },
  { q: "How does Bitdefender GravityZone handle remote and hybrid workforce security?", a: "GravityZone is built for distributed environments — the Bitdefender endpoint agent operates fully autonomously without requiring VPN connectivity to a corporate network or controller, maintaining all protection capabilities including behavioral detection, ML-based anti-malware, network attack defense, and content filtering when working from home or traveling. GravityZone Cloud (SaaS) management means endpoints communicate directly with the GravityZone cloud console without requiring network routing through corporate infrastructure. For BYOD scenarios, GravityZone can be deployed in configurations that protect work applications without accessing personal data. Risk Analytics provides visibility into which remote endpoints have unpatched vulnerabilities, outdated agents, or risky user behaviors — allowing prioritized remediation regardless of physical location. ALG Data Guard configures GravityZone policies appropriate for remote work scenarios including content control policies, VPN-split-tunnel security controls, and encryption enforcement for endpoints leaving corporate networks." },
  { q: "Can Bitdefender integrate with our existing SIEM and SOAR platforms?", a: "GravityZone provides extensive integration capabilities for security operations platforms. SIEM integrations are available for Splunk, Microsoft Sentinel, IBM QRadar, and Elastic SIEM through native connectors or syslog/CEF event streaming — sending all GravityZone detection events, policy violations, and risk score changes to your central logging infrastructure. SOAR integrations are available for Splunk SOAR, Microsoft Sentinel Playbooks, Palo Alto XSOAR, and ServiceNow Security Operations through GravityZone's REST API — enabling automated response playbooks triggered by GravityZone detections. Ticketing integrations connect GravityZone to ServiceNow, Jira, and PagerDuty for incident management workflow. Identity integrations with Active Directory, Azure AD, Okta, and CrowdStrike Falcon Identity allow GravityZone to incorporate identity context into risk scoring. ALG Data Guard designs and implements these integrations as part of GravityZone deployment — ensuring your security tool ecosystem works together rather than in isolated silos." },
  { q: "How does ALG Data Guard manage Bitdefender GravityZone for clients?", a: "ALG Data Guard provides comprehensive GravityZone managed services: initial deployment and policy configuration aligned to your organizational risk tolerance and compliance requirements, ongoing policy management and tuning as your environment changes, incident investigation and response for GravityZone detections, exclusion management to reduce false positives without creating security gaps, GravityZone platform version management and update coordination, monthly security reporting covering detection trends, risk score changes, and endpoint protection status, threat intelligence review with policy updates when new threat techniques require configuration changes, and quarterly security posture reviews with prioritized improvement recommendations. We also assist with GravityZone integration with your SIEM, SOAR, and ticketing platforms — creating a unified security operations workflow rather than managing GravityZone as an isolated tool." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Bitdefender() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(500, 2200, metricsInView);
  const c2 = useCounter(30, 2000, metricsInView);
  const c3 = useCounter(400, 2200, metricsInView);
  const c4 = useCounter(50, 2000, metricsInView);

  return (
    <div className="bd-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band" style={{ background:"#111", borderBottom:"1px solid rgba(238,28,37,0.2)" }}>
        <div className="pd-identity-inner">
          <img src={bitdefenderLogo} alt="Bitdefender" className="pd-partner-logo" />
          <div className="pd-identity-divider" style={{ background:"rgba(255,255,255,0.12)" }} aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow" style={{ color:"rgba(255,255,255,0.6)", background:"rgba(238,28,37,0.12)", borderColor:"rgba(238,28,37,0.25)" }}>
              <span className="pd-live-dot" style={{ background:"#ee1c25" }} />
              Endpoint Security & Cybersecurity Partner
            </div>
            <h1 className="pd-identity-name" style={{ color:"#fff" }}>ALG Data Guard × Bitdefender</h1>
            <p className="pd-identity-desc" style={{ color:"rgba(255,255,255,0.65)" }}>
              Bitdefender is one of the world's most awarded cybersecurity companies — consistently top-ranked by independent testing organizations AV-TEST and AV-Comparatives for detection rate, performance, and false positive rate. Bitdefender GravityZone delivers unified endpoint protection, EDR, XDR, and MDR through a single cloud platform — protecting endpoints from ransomware, advanced persistent threats, zero-days, and fileless malware using layered AI detection. ALG Data Guard is an authorized Bitdefender partner providing GravityZone deployment, management, and managed security services.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="bd-hero">
        <div className="bd-hero__scanline" aria-hidden="true" />
        <div className="bd-hero__grid" aria-hidden="true" />
        <div className="bd-hero__split" aria-hidden="true" />
        <div className="bd-hero__inner">
          {/* Left */}
          <div>
            <div className="bd-label bd-label--red"><span className="bd-dot" /> Bitdefender Authorized Partner</div>
            <h2 className="bd-hero__title">
              Stop Threats<br />Before They<br /><em>Become Breaches.</em>
            </h2>
            <p className="bd-hero__body">
              Bitdefender GravityZone combines AI-powered endpoint protection, behavioral EDR, and extended XDR to stop ransomware, APTs, and zero-day attacks in real time — across every endpoint, cloud workload, and identity in your environment. ALG Data Guard deploys and manages GravityZone for organizations that cannot afford a security incident.
            </p>
            <div className="bd-hero__actions">
              <button className="bd-btn bd-btn--red" onClick={() => navigate("/contact")}>
                Deploy GravityZone <ArrowIcon />
              </button>
              <button className="bd-btn bd-btn--outline" onClick={() => navigate("/contact")}>
                Security Assessment
              </button>
            </div>
            <div className="bd-hero__proof">
              <div><div className="bd-hero__proof-val">#1</div><div className="bd-hero__proof-label">Ranked by AV-TEST</div></div>
              <div><div className="bd-hero__proof-val">500M+</div><div className="bd-hero__proof-label">Endpoints Protected</div></div>
              <div><div className="bd-hero__proof-val">30+</div><div className="bd-hero__proof-label">AV-C "Best" Awards</div></div>
            </div>
          </div>

          {/* Right — SOC Threat Panel */}
          <div className="bd-soc-panel">
            <div className="bd-soc-header">
              <span className="bd-soc-title">GravityZone — Threat Intelligence Feed</span>
              <span className="bd-soc-live"><span className="bd-soc-dot" /> LIVE</span>
            </div>
            <div className="bd-threat-feed">
              {[
                { sev:"CRIT", cls:"crit", text:"🔴 Ransomware — MedusaLocker variant detected, process tree terminated", status:"BLOCKED", statusCls:"blocked" },
                { sev:"HIGH", cls:"high", text:"🟡 PowerShell LOLbin execution — base64 encoded payload, memory scan triggered", status:"QUARANTINE", statusCls:"quarantine" },
                { sev:"MED", cls:"med", text:"🔵 Suspicious outbound connection attempt — C2 IP reputation match", status:"BLOCKED", statusCls:"blocked" },
                { sev:"HIGH", cls:"high", text:"🟡 Credential dump attempt — LSASS process access from non-system process", status:"BLOCKED", statusCls:"blocked" },
                { sev:"MED", cls:"med", text:"🔵 USB mass storage device — policy enforcement triggered, device blocked", status:"BLOCKED", statusCls:"blocked" },
              ].map(t => (
                <div key={t.text} className="bd-threat-entry">
                  <div className={`bd-threat-sev bd-threat-sev--${t.cls}`}>{t.sev}</div>
                  <span className="bd-threat-text">{t.text}</span>
                  <span className={`bd-threat-status-b bd-threat-status-b--${t.statusCls}`}>{t.status}</span>
                </div>
              ))}
            </div>
            <div className="bd-risk-gauges">
              <div className="bd-gauge">
                <div className="bd-gauge__val bd-gauge__val--green">98.7%</div>
                <div className="bd-gauge__label">Protection Rate</div>
              </div>
              <div className="bd-gauge">
                <div className="bd-gauge__val bd-gauge__val--red">4</div>
                <div className="bd-gauge__label">Active Threats</div>
              </div>
              <div className="bd-gauge">
                <div className="bd-gauge__val bd-gauge__val--yellow">12</div>
                <div className="bd-gauge__label">Risk Findings</div>
              </div>
            </div>
            <div className="bd-endpoint-bar">
              <div className="bd-endpoint-bar__label">
                <span>Endpoint Protection Coverage</span>
                <span style={{ color:"#22c55e" }}>1,847 / 1,850</span>
              </div>
              <div className="bd-endpoint-bar__track">
                <div className="bd-endpoint-bar__fill" style={{ width:"99.8%" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Threat Landscape ── */}
      <section className="bd-landscape">
        <div className="bd-shell">
          <div className="bd-landscape__inner">
            {[
              { val:"400K+", label:"New Malware Samples Daily" },
              { val:"$4.9M", label:"Avg Cost of a Data Breach" },
              { val:"277 Days", label:"Avg Breach Detection Time" },
              { val:"71%", label:"Ransomware Uses Credential Theft" },
            ].map(s => (
              <div key={s.val} className="bd-landscape-stat">
                <div className="bd-landscape-stat__val">{s.val}</div>
                <div className="bd-landscape-stat__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Endpoint Protection ── */}
      <section className="bd-endpoint">
        <div className="bd-shell">
          <div className="bd-endpoint__inner">
            <div>
              <div className="bd-label bd-label--red"><span className="bd-dot" /> Endpoint Protection</div>
              <h2 className="bd-title">GravityZone — Layered Defense at Every Level</h2>
              <p className="bd-endpoint__body">
                Bitdefender GravityZone Business Security Enterprise delivers the industry's most comprehensive endpoint protection stack — combining signature-less behavioral detection, machine learning, network attack defense, and exploit prevention in a single lightweight agent. Each protection layer operates independently, ensuring detection when individual layers are bypassed.
              </p>
              <p className="bd-endpoint__body">
                ALG Data Guard deploys GravityZone with policy configurations tailored to your organizational risk tolerance — balancing aggressive threat detection with operational continuity for production endpoints where false positives carry real business impact.
              </p>
              <div className="bd-endpoint__layers">
                {endpointLayers.map(l => (
                  <div key={l.name} className="bd-layer">
                    <span className="bd-layer__icon">{l.icon}</span>
                    <div>
                      <div className="bd-layer__name">{l.name}</div>
                      <p className="bd-layer__desc">{l.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="bd-btn bd-btn--red" onClick={() => navigate("/contact")}>
                  Deploy GravityZone EDR <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80"
              alt="Bitdefender endpoint security"
              className="bd-endpoint__img"
            />
          </div>
        </div>
      </section>

      {/* ── XDR Platform ── */}
      <section className="bd-xdr">
        <div className="bd-shell">
          <div className="bd-xdr__head">
            <div className="bd-label bd-label--red"><span className="bd-dot" /> XDR Platform</div>
            <h2 className="bd-title">GravityZone XDR — Unified Detection Across Every Layer</h2>
            <p className="bd-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>GravityZone XDR eliminates detection silos — correlating endpoint, network, email, cloud, and identity telemetry into unified attack timelines that expose multi-stage threats invisible to single-layer detection.</p>
          </div>
          <div className="bd-xdr__grid">
            {xdrCards.map(card => (
              <div key={card.title} className="bd-xdr-card">
                <span className="bd-xdr-card__icon">{card.icon}</span>
                <h3 className="bd-xdr-card__title">{card.title}</h3>
                <p className="bd-xdr-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MDR ── */}
      <section className="bd-mdr">
        <div className="bd-shell">
          <div className="bd-mdr__inner">
            <img
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=900&q=80"
              alt="Bitdefender MDR security operations"
              className="bd-mdr__img"
            />
            <div>
              <div className="bd-label bd-label--red"><span className="bd-dot" /> Managed Detection & Response</div>
              <h2 className="bd-title">Bitdefender MDR — Your 24/7 SOC Team</h2>
              <p className="bd-mdr__body">
                Bitdefender Managed Detection and Response delivers a fully-staffed 24/7 Security Operations Center as a service — Bitdefender's expert analysts monitor your GravityZone environment, investigate every alert, hunt for threats, and actively respond to contain incidents before they spread.
              </p>
              <p className="bd-mdr__body">
                ALG Data Guard positions Bitdefender MDR as the security operations layer for organizations that need enterprise-grade SOC capabilities without the cost of building and staffing an internal 24/7 operation. MDR analysts can directly contain threats through GravityZone with average containment times under 30 minutes.
              </p>
              <div className="bd-mdr__sla">
                {mdrSlas.map(s => (
                  <div key={s.val} className="bd-sla-box">
                    <div className="bd-sla-box__val">{s.val}</div>
                    <div className="bd-sla-box__label">{s.label}</div>
                  </div>
                ))}
              </div>
              <button className="bd-btn bd-btn--red" onClick={() => navigate("/contact")}>
                Start MDR Service <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Security ── */}
      <section className="bd-ai">
        <div className="bd-shell">
          <div className="bd-ai__inner">
            <div>
              <div className="bd-label bd-label--red"><span className="bd-dot" /> AI-Powered Security</div>
              <h2 className="bd-title">Detection That Learns. Defenses That Evolve.</h2>
              <p className="bd-ai__body">
                Bitdefender's AI security stack is trained on data from 500M+ protected endpoints worldwide — the largest endpoint threat intelligence corpus in the industry. Every new threat discovered anywhere in this global network improves detection for every protected endpoint simultaneously.
              </p>
              <p className="bd-ai__body">
                Unlike rule-based detection that attackers learn to bypass, Bitdefender's machine learning models detect malicious behavioral patterns regardless of specific payload signatures — making Bitdefender effective against novel ransomware families, living-off-the-land attacks, and custom-developed malware targeting your industry.
              </p>
              <div className="bd-ai__capabilities">
                {aiCaps.map(cap => (
                  <div key={cap.title} className="bd-ai-cap">
                    <span className="bd-ai-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="bd-ai-cap__title">{cap.title}</div>
                      <p className="bd-ai-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="Bitdefender AI security analytics"
              className="bd-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Compliance ── */}
      <section className="bd-compliance">
        <div className="bd-shell">
          <div className="bd-compliance__head">
            <div className="bd-label bd-label--red"><span className="bd-dot" /> Compliance</div>
            <h2 className="bd-title">Meeting Every Regulatory Framework</h2>
            <p className="bd-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>GravityZone's technical security controls map directly to every major regulatory framework — with built-in compliance reporting reducing audit preparation time from weeks to hours.</p>
          </div>
          <div className="bd-compliance__frameworks">
            {complianceFrameworks.map(f => (
              <div key={f.name} className="bd-framework">
                <span className="bd-framework__icon">{f.icon}</span>
                <div className="bd-framework__name">{f.name}</div>
                <p className="bd-framework__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="bd-industries">
        <div className="bd-shell">
          <div className="bd-industries__head">
            <div className="bd-label bd-label--red"><span className="bd-dot" /> Industries</div>
            <h2 className="bd-title">Security Built for Every Sector</h2>
            <p className="bd-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Bitdefender GravityZone protects organizations across every regulated industry — with compliance-specific configurations, industry threat intelligence, and specialized deployment templates.</p>
          </div>
          <div className="bd-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="bd-ind-card">
                <span className="bd-ind-card__icon">{ind.icon}</span>
                <h3 className="bd-ind-card__name">{ind.name}</h3>
                <p className="bd-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="bd-metrics" ref={metricsRef}>
        <div className="bd-shell">
          <div className="bd-metrics__inner">
            <div><div className="bd-metric__val">{c1}M+</div><div className="bd-metric__label">Endpoints Protected Globally</div></div>
            <div><div className="bd-metric__val">{c2}+</div><div className="bd-metric__label">AV-C Best Product Awards</div></div>
            <div><div className="bd-metric__val">{c3}M+</div><div className="bd-metric__label">Threats Blocked Daily</div></div>
            <div><div className="bd-metric__val">{c4}+</div><div className="bd-metric__label">Countries Protected</div></div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bd-faq">
        <div className="bd-shell">
          <div className="bd-faq__head">
            <div className="bd-label bd-label--red" style={{ margin:"0 auto 1rem" }}><span className="bd-dot" /> FAQs</div>
            <h2 className="bd-title">Security Questions, Answered</h2>
            <p className="bd-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about Bitdefender GravityZone, XDR, MDR, and ALG Data Guard's security deployment services.</p>
          </div>
          <div className="bd-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`bd-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="bd-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="bd-faq-item__q">{faq.q}</span>
                  <span className="bd-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="bd-faq-item__answer"><div className="bd-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bd-cta">
        <div className="bd-cta__inner">
          <div className="bd-label bd-label--red" style={{ margin:"0 auto 1.2rem" }}><span className="bd-dot" /> Get Protected</div>
          <h2 className="bd-cta__title">
            Your Endpoints Need <em>Uncompromising</em> Protection.
          </h2>
          <p className="bd-cta__sub">Whether you need to deploy Bitdefender GravityZone across your organization, extend detection with XDR, or add 24/7 MDR coverage — ALG Data Guard's certified security team designs and manages Bitdefender implementations built for your threat environment.</p>
          <div className="bd-cta__actions">
            <button className="bd-btn bd-btn--red" onClick={() => navigate("/contact")}>
              Deploy GravityZone Now <ArrowIcon />
            </button>
            <button className="bd-btn bd-btn--slate" onClick={() => navigate("/contact")}>
              Start Free Security Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
